import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@socialmanagement/types'
import { setTokenGetter, setTokenSetter, setClearAuth } from '@/lib/api'

// Extend the base User type with Discord fields
export interface AuthUser extends User {
  discordId?: string | null
  discordUsername?: string | null
  discordAvatar?: string | null
}

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  isAuthenticated: boolean
  isLoading: boolean

  setAuth: (user: AuthUser, accessToken: string) => void
  setAccessToken: (token: string) => void
  clearAuth: () => void
  setLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: true,

      setAuth: (user, accessToken) => {
        set({ user, accessToken, isAuthenticated: true, isLoading: false })
      },

      setAccessToken: (token) => {
        set({ accessToken: token })
      },

      clearAuth: () => {
        set({ user: null, accessToken: null, isAuthenticated: false, isLoading: false })
      },

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'sm-auth',
      partialize: (state) => ({
        // Don't persist accessToken (short-lived); just persist user info
        // The access token is refreshed on app load via the /auth/refresh endpoint
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)

// Wire up token getter/setter and clear for the API client (enables silent 401 refresh)
setTokenGetter(() => useAuthStore.getState().accessToken)
setTokenSetter((token) => useAuthStore.getState().setAccessToken(token))
setClearAuth(() => useAuthStore.getState().clearAuth())
